/**
 * Live editing of END-TO-END ENCRYPTED notes.
 *
 * The Yjs document lives only in this tab. Each local change is encrypted and sent to the
 * server's relay (/enc-collab), which stores and forwards ciphertext without being able to read
 * it. Two documents are kept: `doc` (what the editor shows) and `server` (only what the server
 * has confirmed). Whatever `doc` has beyond `server` is unsynced and is re-sent after reconnects,
 * so no edit is lost to network drops. The offline copy in IndexedDB is ciphertext only.
 */
import * as Y from "yjs";
import { get, post, put, ApiError } from "@/lib/api";
import { idbDel, idbGet, idbSet } from "@/lib/idb";
import { notify, peekEntry, registerEntry, setEntry, type CollabEntry, type SyncState } from "@/features/notes/collab";
import { decSnapshot, decUpdate, encSnapshot, encUpdate, encVersion, proofFor } from "./ops";
import { onLock } from "./keyring";

const REMOTE = Symbol("remote");
const COMPACT_EVERY = 50;
const AUTO_VERSION_MS = 10 * 60 * 1000;

interface EncState {
  seq: number;
  snapshot: { blob: string; upToSeq: number } | null;
  updates: { seq: number; blob: string }[];
}

interface CachedCopy {
  seq: number;
  server: string;
  local: string;
}

// Shared relay socket -----------------------------------------------------------------
class RelaySocket {
  private ws: WebSocket | null = null;
  private open = false;
  private retry = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  sessions = new Map<string, EncryptedNoteSession>();

  add(s: EncryptedNoteSession) {
    this.sessions.set(s.noteId, s);
    if (!this.ws) this.connect();
    else if (this.open) s.onSocketOpen();
  }

  remove(noteId: string, session: EncryptedNoteSession) {
    if (this.sessions.get(noteId) !== session) return;
    this.sessions.delete(noteId);
    this.send({ type: "unsubscribe", noteId });
    if (!this.sessions.size) {
      this.ws?.close();
      this.ws = null;
      this.open = false;
      if (this.timer) clearTimeout(this.timer);
    }
  }

  send(msg: unknown) {
    if (!this.open || !this.ws) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  get isOpen() {
    return this.open;
  }

  private connect() {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${location.host}/enc-collab`);
    this.ws = ws;
    ws.onopen = () => {
      this.open = true;
      this.retry = 0;
      for (const s of this.sessions.values()) s.onSocketOpen();
    };
    ws.onmessage = (ev) => {
      let msg: any;
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      this.sessions.get(msg.noteId)?.onMessage(msg);
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.open = false;
      this.ws = null;
      for (const s of this.sessions.values()) s.onSocketClosed();
      if (this.sessions.size) {
        const delay = Math.min(10_000, 1000 * 2 ** this.retry++);
        this.timer = setTimeout(() => {
          if (!this.ws && this.sessions.size) this.connect();
        }, delay);
      }
    };
  }
}

const relay = new RelaySocket();

// Session ------------------------------------------------------------------------------
export class EncryptedNoteSession implements CollabEntry {
  kind = "encrypted" as const;
  doc = new Y.Doc();
  awareness = null;
  state: SyncState = "connecting";
  lastSavedAt: string | null = null;
  error: string | null = null;
  everSynced = false;
  fromOfflineCopy = false;
  listeners = new Set<() => void>();
  /** Current decrypted title, used for automatic encrypted versions. */
  title = "";

  private server = new Y.Doc();
  private known = new Set<number>();
  private contiguous = 0;
  private snapshotSeq = 0;
  private inflight = new Map<string, Uint8Array>();
  private subscribed = false;
  private destroyed = false;
  private chain: Promise<unknown> = Promise.resolve();
  private cacheTimer: ReturnType<typeof setTimeout> | null = null;
  private lastVersionAt = Date.now();
  private editedSinceVersion = false;
  private unsubscribeLock: () => void;
  private skipped = 0;

  constructor(
    public noteId: string,
    public scopeId: string,
    private userId: string,
    public epoch: number,
  ) {
    this.doc.on("update", (u: Uint8Array, origin: unknown) => {
      if (origin === REMOTE || this.destroyed) return;
      this.editedSinceVersion = true;
      this.scheduleCache();
      this.queue(() => this.sendUpdate(u));
    });
    this.unsubscribeLock = onLock((ids) => {
      if (ids.includes(scopeId)) this.lockNow();
    });
    void this.start();
  }

  private get cacheKey() {
    return `enc:${this.noteId}:${this.epoch}`;
  }

  private set(patch: Partial<CollabEntry>) {
    if (this.destroyed) return;
    setEntry(this, patch);
  }

  private queue<T>(fn: () => Promise<T>) {
    const next = this.chain.then(fn, fn);
    this.chain = next.catch(() => undefined);
    return next;
  }

  get isDestroyed() {
    return this.destroyed;
  }

  hasUnsynced() {
    if (this.inflight.size) return true;
    return Y.encodeStateAsUpdate(this.doc, Y.encodeStateVector(this.server)).length > 2;
  }

  private markKnown(seq: number) {
    this.known.add(seq);
    while (this.known.has(this.contiguous + 1)) {
      this.known.delete(this.contiguous + 1);
      this.contiguous++;
    }
  }

  private applyRemote(update: Uint8Array) {
    Y.applyUpdate(this.server, update, REMOTE);
    Y.applyUpdate(this.doc, update, REMOTE);
  }

  private async applyState(st: EncState) {
    if (st.snapshot) {
      this.applyRemote(await decSnapshot(this.scopeId, this.noteId, st.snapshot.blob));
      this.snapshotSeq = Math.max(this.snapshotSeq, st.snapshot.upToSeq);
      if (st.snapshot.upToSeq > this.contiguous) this.contiguous = st.snapshot.upToSeq;
    }
    for (const u of st.updates) {
      await this.applyEncryptedUpdate(u.blob);
      this.markKnown(u.seq);
    }
  }

  /** An update that does not decrypt (damaged, or not made with this key) is skipped, not fatal. */
  private async applyEncryptedUpdate(blob: string) {
    try {
      this.applyRemote(await decUpdate(this.scopeId, this.noteId, blob));
    } catch {
      this.skipped++;
      this.error = `${this.skipped} change${this.skipped === 1 ? "" : "s"} could not be decrypted with this key and ${this.skipped === 1 ? "was" : "were"} skipped.`;
    }
  }

  private async start() {
    try {
      const cached = await idbGet<CachedCopy>(this.userId, this.cacheKey);
      if (cached && !this.destroyed) {
        this.applyRemote(await decSnapshot(this.scopeId, this.noteId, cached.server));
        Y.applyUpdate(this.doc, await decSnapshot(this.scopeId, this.noteId, cached.local), "offline-copy");
        this.contiguous = cached.seq;
        this.snapshotSeq = cached.seq;
        if (this.doc.getText("content").length) this.set({ fromOfflineCopy: true });
      }
    } catch {
      // A damaged or foreign offline copy is ignored; the server state is authoritative.
      await idbDel(this.userId, this.cacheKey);
    }
    relay.add(this);
    if (!relay.isOpen) await this.catchUp().catch(() => undefined);
  }

  /** Fetch whatever the server has beyond what we know, then send what the server lacks. */
  private catchUp() {
    return this.queue(async () => {
      if (this.destroyed) return;
      try {
        const known = this.contiguous > 0 || this.everSynced || this.snapshotSeq > 0;
        const st = await get<EncState>(`/api/notes/${this.noteId}/enc-state${known ? `?after=${this.contiguous}` : ""}`);
        await this.applyState(st);
        this.set({ everSynced: true, fromOfflineCopy: false });
        await this.sendMissing();
        this.refreshState();
        this.scheduleCache();
      } catch (err) {
        if (err instanceof ApiError && [403, 404, 409].includes(err.status)) {
          await idbDel(this.userId, this.cacheKey);
          this.set({ state: err.status === 409 ? "revoked" : "forbidden", error: err.status === 409 ? "This note is no longer encrypted. Reopen it." : "You do not have access to this note." });
          return;
        }
        this.set({ state: "offline" });
      }
    });
  }

  private async sendMissing() {
    const diff = Y.encodeStateAsUpdate(this.doc, Y.encodeStateVector(this.server));
    if (diff.length > 2) await this.sendUpdate(diff);
  }

  private async sendUpdate(u: Uint8Array) {
    if (this.destroyed) return;
    if (!this.subscribed || !relay.isOpen) {
      this.refreshState();
      this.scheduleRestFlush();
      return;
    }
    const ref = crypto.randomUUID();
    this.inflight.set(ref, u);
    const blob = await encUpdate(this.scopeId, this.noteId, u);
    if (!relay.send({ type: "update", noteId: this.noteId, ref, blob })) this.inflight.delete(ref);
    this.refreshState();
  }

  private restTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Fallback when the live relay is unavailable (e.g. a proxy without WebSocket support): send
   * what the server lacks over plain HTTP, then pick up others' changes the same way.
   */
  private scheduleRestFlush() {
    if (this.restTimer || this.destroyed) return;
    this.restTimer = setTimeout(() => {
      this.restTimer = null;
      if (this.destroyed || (relay.isOpen && this.subscribed)) return;
      void this.queue(async () => {
        try {
          const diff = Y.encodeStateAsUpdate(this.doc, Y.encodeStateVector(this.server));
          if (diff.length > 2) {
            const blob = await encUpdate(this.scopeId, this.noteId, diff);
            const r = await post<{ seqs: number[] }>(`/api/notes/${this.noteId}/enc-updates`, { blobs: [blob], keyProof: proofFor(this.scopeId) });
            Y.applyUpdate(this.server, diff, REMOTE);
            r.seqs.forEach((q) => this.markKnown(q));
            this.lastSavedAt = new Date().toISOString();
          }
          await this.applyState(await get<EncState>(`/api/notes/${this.noteId}/enc-state?after=${this.contiguous}`));
          this.set({ everSynced: true, state: this.hasUnsynced() ? "saving" : "saved", error: null });
          this.scheduleCache();
        } catch {
          this.set({ state: "offline" });
        }
        if (!relay.isOpen || !this.subscribed) this.scheduleRestFlush();
      });
    }, 2500);
  }

  private refreshState() {
    if (this.destroyed || ["forbidden", "revoked", "locked", "save-error"].includes(this.state)) return;
    if (!relay.isOpen || !this.subscribed) {
      // Saved over HTTP while the live relay is down: not "offline" unless that also fails.
      if (this.state === "saved" && !this.hasUnsynced()) return;
      return this.set({ state: this.everSynced ? (this.hasUnsynced() ? "saving" : "offline") : "connecting" });
    }
    this.set({ state: this.hasUnsynced() ? "saving" : this.lastSavedAt ? "saved" : "synced" });
  }

  onSocketOpen() {
    // Only key holders can subscribe: the proof shows this tab has the key.
    try {
      relay.send({ type: "subscribe", noteId: this.noteId, keyProof: proofFor(this.scopeId) });
    } catch {
      /* locked meanwhile */
    }
  }

  onSocketClosed() {
    this.subscribed = false;
    this.inflight.clear();
    this.refreshState();
    this.scheduleRestFlush();
  }

  onMessage(msg: any) {
    if (this.destroyed) return;
    switch (msg.type) {
      case "subscribed":
        this.subscribed = true;
        void this.catchUp();
        break;
      case "update":
        void this.queue(async () => {
          await this.applyEncryptedUpdate(msg.blob);
          this.markKnown(msg.seq);
          this.scheduleCache();
        });
        break;
      case "ack": {
        const u = this.inflight.get(msg.ref);
        if (u) {
          Y.applyUpdate(this.server, u, REMOTE);
          this.inflight.delete(msg.ref);
        }
        this.markKnown(msg.seq);
        this.lastSavedAt = new Date().toISOString();
        this.refreshState();
        this.scheduleCache();
        if (!this.inflight.size) void this.maintenance();
        break;
      }
      case "error":
        if (msg.reason === "not-subscribed") {
          this.subscribed = false;
          this.onSocketOpen();
        } else if (msg.ref) {
          this.inflight.delete(msg.ref);
          this.set({ state: "save-error", error: msg.message ?? "An encrypted change could not be saved. BrainLog will try again." });
          setTimeout(() => {
            if (this.state === "save-error") this.set({ state: "saving" });
            void this.catchUp();
          }, 3000);
        }
        break;
      case "denied":
      case "revoked":
        void idbDel(this.userId, this.cacheKey);
        this.set({ state: msg.type === "denied" && !this.everSynced ? "forbidden" : "revoked", error: "Your access to this note was removed." });
        break;
      case "encryption-changed":
        void idbDel(this.userId, this.cacheKey);
        this.set({ state: "revoked", error: "This note is no longer encrypted. Reopen it to continue." });
        break;
    }
  }

  /** Compaction and automatic versions, done by whichever editor is active. */
  private maintenance() {
    return this.queue(async () => {
      if (this.destroyed) return;
      if (this.contiguous - this.snapshotSeq >= COMPACT_EVERY) {
        const upTo = this.contiguous;
        const blob = await encSnapshot(this.scopeId, this.noteId, Y.encodeStateAsUpdate(this.server));
        await put(`/api/notes/${this.noteId}/enc-snapshot`, { blob, upToSeq: upTo, keyProof: proofFor(this.scopeId) }).catch(() => undefined);
        this.snapshotSeq = upTo;
      }
      if (this.editedSinceVersion && Date.now() - this.lastVersionAt > AUTO_VERSION_MS) await this.saveVersion("auto");
    });
  }

  /** Store an encrypted version (title + text) of the current content. */
  async saveVersion(reason: "manual" | "auto" | "restore" | "before-restore", restoredFromVersionId?: string) {
    const id = crypto.randomUUID();
    const blob = await encVersion(this.scopeId, this.noteId, id, { title: this.title, text: this.doc.getText("content").toString() });
    const res = await post(`/api/notes/${this.noteId}/enc-versions`, { id, blob, reason, restoredFromVersionId: restoredFromVersionId ?? null, keyProof: proofFor(this.scopeId) });
    this.lastVersionAt = Date.now();
    this.editedSinceVersion = false;
    return res;
  }

  private scheduleCache() {
    if (this.cacheTimer) clearTimeout(this.cacheTimer);
    this.cacheTimer = setTimeout(() => void this.writeCache(), 1500);
  }

  private writeCache() {
    if (this.destroyed || !offlineEnabled()) return Promise.resolve();
    // Both states and the key are captured synchronously, so this also works while locking.
    const seq = this.contiguous;
    let server: Promise<string>, local: Promise<string>;
    try {
      server = encSnapshot(this.scopeId, this.noteId, Y.encodeStateAsUpdate(this.server));
      local = encSnapshot(this.scopeId, this.noteId, Y.encodeStateAsUpdate(this.doc));
    } catch {
      return Promise.resolve();
    }
    return Promise.all([server, local])
      .then(([s, l]) => idbSet(this.userId, this.cacheKey, { seq, server: s, local: l } satisfies CachedCopy))
      .catch(() => undefined);
  }

  private lockNow() {
    // The key is about to be dropped: the decrypted document leaves memory. Unsynced edits are
    // kept in the encrypted offline copy and re-sent after unlocking.
    if (this.cacheTimer) clearTimeout(this.cacheTimer);
    this.cacheTimer = null;
    void this.writeCache();
    this.set({ state: "locked", error: "Locked. Enter the passphrase to continue." });
    this.destroy();
  }

  destroy() {
    if (this.destroyed) return;
    if (this.cacheTimer) {
      clearTimeout(this.cacheTimer);
      void this.writeCache();
    }
    this.destroyed = true;
    if (this.restTimer) clearTimeout(this.restTimer);
    this.unsubscribeLock();
    relay.remove(this.noteId, this);
    this.doc.destroy();
    this.server.destroy();
    notify(this);
  }
}

function offlineEnabled() {
  try {
    return localStorage.getItem("brainlog-claude.offline-copies") !== "off";
  } catch {
    return true;
  }
}

export function getEncryptedCollab(noteId: string, scopeId: string, user: { id: string }, epoch: number): EncryptedNoteSession {
  // The registry is updated synchronously, so repeated calls (e.g. React's dev double render) reuse one session.
  const existing = peekEntry(noteId);
  if (existing instanceof EncryptedNoteSession && existing.scopeId === scopeId && existing.epoch === epoch && !existing.isDestroyed) return existing;
  const s = new EncryptedNoteSession(noteId, scopeId, user.id, epoch);
  registerEntry(s);
  return s;
}
